import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, LogIn, MapPin, Package, Shield, UserPlus } from 'lucide-react';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import { marketplaceService } from '../../services';
import { useUser } from '../../UserContext';
import '../../platform/pages/dashboard/Marketplace/ListingDetail.css';

const PublicListingPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { initialized, isAuthenticated } = useUser();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [listing, setListing] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        const loadListing = async () => {
            setIsLoading(true);
            setError('');
            try {
                const data = await marketplaceService.getSharedListing(token);
                setListing(data);
            } catch (loadError) {
                setError(loadError.message || 'Unable to load this shared marketplace listing.');
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            loadListing();
        }
    }, [token]);

    useEffect(() => {
        const resolve = async () => {
            if (!initialized || !isAuthenticated || !token) return;
            try {
                const data = await marketplaceService.resolveSharedListing(token);
                if (data?.dashboardUrl) {
                    navigate(data.dashboardUrl, { replace: true });
                }
            } catch {
                // Fall back to public page if resolution fails.
            }
        };

        resolve();
    }, [initialized, isAuthenticated, navigate, token]);

    const images = useMemo(() => listing?.imageUrls?.length ? listing.imageUrls : ['/placeholder-equipment.jpg'], [listing?.imageUrls]);

    if (isLoading) {
        return <div className="listing-detail-loading"><div className="loading-spinner"></div><p>Loading listing...</p></div>;
    }

    if (error || !listing) {
        return (
            <div className="listing-detail-error">
                <h2>Listing not available</h2>
                <p>{error || 'This listing is no longer available.'}</p>
            </div>
        );
    }

    return (
        <div className="home-page">
            <Header openLoginModal={() => setIsLoginModalOpen(true)} isAuthenticated={initialized ? isAuthenticated : null} alwaysSolid />
            <main className="listing-detail-page" style={{ paddingTop: 24, paddingBottom: 56 }}>
                <Link to="/" className="back-button"><ArrowLeft size={20} /> Back home</Link>

                {listing.requiresCertificateVerification && (
                    <div className="compliance-banner">
                        <Shield size={24} />
                        <div className="compliance-content">
                            <h3>Restricted marketplace item</h3>
                            <p>This item requires certificate verification before any transfer can happen.</p>
                        </div>
                    </div>
                )}

                <div className="listing-detail-content">
                    <div className="listing-gallery">
                        <div className="main-image-container">
                            <img src={images[currentImageIndex]} alt={listing.title} className="main-image" />
                        </div>
                        {images.length > 1 && (
                            <div className="thumbnail-strip">
                                {images.map((imageUrl, index) => (
                                    <button key={`${imageUrl}-${index}`} className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(index)}>
                                        <img src={imageUrl} alt={`Listing ${index + 1}`} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="listing-info">
                        <div className="listing-header">
                            <div className="listing-title-section">
                                <span className="listing-category">{listing.category}</span>
                                <h1>{listing.title}</h1>
                                {(listing.make || listing.model) && <p className="listing-brand">{listing.make || ''} {listing.model ? `- ${listing.model}` : ''}</p>}
                            </div>
                        </div>

                        <div className="listing-price-section">
                            <div className="price-display">
                                <span className="price">GBP {Number(listing.price || 0).toLocaleString('en-GB')}</span>
                                <span className="accepts-offers"><Package size={16} /> {listing.listingType || 'Sale'}</span>
                            </div>
                        </div>

                        <div className="listing-meta">
                            <div className="meta-item"><MapPin size={18} /><span>{listing.location || 'Location not specified'}</span></div>
                            <div className="meta-item"><Calendar size={18} /><span>Listed {new Date(listing.createdAt).toLocaleDateString('en-GB')}</span></div>
                            <div className="meta-item"><Package size={18} /><span>{listing.collectionOnly ? 'Collection only' : 'Delivery available'}</span></div>
                        </div>

                        <div className="listing-description">
                            <h3>Description</h3>
                            <p>{listing.description}</p>
                        </div>

                        <div className="seller-card">
                            <div className="seller-header">
                                <div className="seller-info">
                                    <h4>{listing.sellerName}</h4>
                                    <p className="member-since">{listing.sellerType === 'Venue' ? 'Venue seller' : 'Member seller'}</p>
                                    {listing.publicVenueUrl ? <Link to={listing.publicVenueUrl} className="btn-secondary" style={{ marginTop: 8 }}>View venue profile</Link> : null}
                                </div>
                            </div>
                        </div>

                        <div className="listing-actions">
                            <button className="btn-primary" onClick={() => setIsLoginModalOpen(true)}><LogIn size={20} /> Log In</button>
                            <Link className="btn-secondary" to="/register"><UserPlus size={20} /> Sign Up</Link>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
        </div>
    );
};

export default PublicListingPage;
